import { useState, useEffect, useCallback, lazy, Suspense } from 'react'
const AdminAccessControl = lazy(() => import('./AdminAccessControl'))
import {
  Shield,
  Lock,
  Eye,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileText,
  Video,
  Download,
  RefreshCcw,
  LogIn,
  LogOut,
  UserPlus,
  Settings,
  Search,
} from 'lucide-react'
import { adminApi } from '../../lib/api/admin'

interface AuditLogEntry {
  id: number
  user_id: number | null
  user_email: string | null
  action: string
  resource_type: string | null
  ip_address: string | null
  status: string
  created_at: string
  extra_metadata: Record<string, unknown> | null
}

const ACTION_LABELS: Record<string, string> = {
  user_login: 'User Login',
  user_login_failed: 'Login Failed',
  user_logout: 'User Logout',
  user_registered: 'User Registered',
}

const ACTION_ICONS: Record<string, JSX.Element> = {
  user_login: <LogIn size={13} />,
  user_login_failed: <XCircle size={13} />,
  user_logout: <LogOut size={13} />,
  user_registered: <UserPlus size={13} />,
}

const ROLE_FILTERS = [
  { key: '', label: 'All Roles' },
  { key: 'admin', label: 'Admin' },
  { key: 'interviewer', label: 'Interviewer' },
  { key: 'client', label: 'Client' },
]

const ACTION_FILTERS = [
  { key: '', label: 'All Actions' },
  { key: 'user_login', label: 'Login' },
  { key: 'user_logout', label: 'Logout' },
  { key: 'user_registered', label: 'Registration' },
]

const storageStats = [
  { type: 'Recordings', count: 0, size: '0 GB', retention: '30 days' },
  { type: 'Transcripts', count: 0, size: '0 MB', retention: '90 days' },
  { type: 'Reports', count: 0, size: '0 MB', retention: '1 year' },
  { type: 'Documents', count: 0, size: '0 MB', retention: '1 year' },
]

const formatAction = (action: string) =>
  ACTION_LABELS[action] ?? action.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

const formatDate = (dt: string) => {
  const d = new Date(dt)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export default function AdminSecurity() {
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [actionFilter, setActionFilter] = useState('')
  const [exporting, setExporting] = useState(false)
  const [showAccessControl, setShowAccessControl] = useState(false)
  const [configureMsg, setConfigureMsg] = useState(false)

  const fetchLogs = useCallback(async (pg: number, role: string, action: string) => {
    setLoading(true)
    setFetchError('')
    try {
      const params: Record<string, unknown> = { page: pg, per_page: 20 }
      if (role) params.role = role
      if (action) params.action = action
      const res = await adminApi.getAuditLog(params)
      setLogs(res.data || [])
      setTotal(res.total ?? 0)
      setTotalPages(res.last_page ?? 1)
    } catch {
      setFetchError('Unable to load audit log. You may not have permission to view this data.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLogs(page, roleFilter, actionFilter)
  }, [page, roleFilter, actionFilter, fetchLogs])

  const handleRoleFilter = (role: string) => {
    setRoleFilter(role)
    setPage(1)
  }

  const handleActionFilter = (action: string) => {
    setActionFilter(action)
    setPage(1)
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      const blob = await adminApi.exportAuditLog()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
    } finally {
      setExporting(false)
    }
  }

  return (
    <div>
      {showAccessControl && (
        <Suspense fallback={null}>
          <AdminAccessControl onClose={() => setShowAccessControl(false)} />
        </Suspense>
      )}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Security & Compliance</h1>
          <p className="page-subtitle">Recording access controls, storage, and audit logs</p>
        </div>
        <button className="btn btn-secondary" onClick={() => fetchLogs(page, roleFilter, actionFilter)} disabled={loading}>
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div className="stats-grid asc-stats-grid">
        <div className="stat-card">
          <div className="icon success"><Shield size={24} /></div>
          <div className="value">Active</div>
          <div className="label">Security Status</div>
        </div>
        <div className="stat-card">
          <div className="icon primary"><Lock size={24} /></div>
          <div className="value">256-bit</div>
          <div className="label">Encryption</div>
        </div>
        <div className="stat-card">
          <div className="icon warning"><Clock size={24} /></div>
          <div className="value">30 days</div>
          <div className="label">Recording Retention</div>
        </div>
        <div className="stat-card">
          <div className="icon primary"><Eye size={24} /></div>
          <div className="value">{total}</div>
          <div className="label">Audit Events</div>
        </div>
      </div>

      {/* Storage & Access Controls */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: 24, marginBottom: 24 }}>

        {/* Storage & Retention */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Storage & Retention</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {configureMsg && (
                <span style={{ fontSize: 13, color: 'var(--warning)', fontWeight: 500 }}>
                  Storage configuration is in progress — coming soon.
                </span>
              )}
              <button
                className="btn btn-secondary"
                style={{ padding: '8px 16px', fontSize: 13 }}
                onClick={() => {
                  setConfigureMsg(true)
                  setTimeout(() => setConfigureMsg(false), 4000)
                }}
              >
                Configure
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {storageStats.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'var(--accent-light)', borderRadius: 10 }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary-navy)' }}>
                  {item.type === 'Recordings' ? <Video size={20} /> : <FileText size={20} />}
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{item.type}</h4>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{item.count} files &middot; {item.size}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', padding: '6px 12px', background: 'white', borderRadius: 6 }}>
                  <Clock size={14} />{item.retention}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Access Controls */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Access Controls</h3>
            <button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: 13 }} onClick={() => setShowAccessControl(true)}>Manage Access</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: <CheckCircle size={18} />, bg: 'rgba(72,187,120,0.1)', color: 'var(--success)', title: 'Admins', desc: 'Full access to all recordings and transcripts' },
              { icon: <AlertTriangle size={18} />, bg: 'rgba(237,137,54,0.1)', color: 'var(--warning)', title: 'Interviewers', desc: 'Access only to their own session recordings' },
              { icon: <XCircle size={18} />, bg: 'rgba(245,101,101,0.1)', color: 'var(--danger)', title: 'Clients', desc: 'No access to interviewer recordings' },
            ].map((item) => (
              <div key={item.title} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: 'var(--accent-light)', borderRadius: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: item.bg, color: item.color }}>
                  {item.icon}
                </div>
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{item.title}</h4>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>Audit Log</h3>
          <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={handleExport} disabled={exporting}>
            <Download size={14} />{exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Role filter */}
          <div style={{ display: 'flex', gap: 6 }}>
            {ROLE_FILTERS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleRoleFilter(key)}
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                  border: '1px solid var(--border)', borderRadius: 16,
                  background: roleFilter === key ? 'var(--primary-navy)' : 'white',
                  color: roleFilter === key ? 'white' : 'var(--text-muted)',
                  transition: 'all 0.15s',
                }}
              >{label}</button>
            ))}
          </div>

          {/* Action filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', background: 'white' }}>
            <Search size={13} style={{ color: 'var(--text-muted)' }} />
            <select
              value={actionFilter}
              onChange={(e) => handleActionFilter(e.target.value)}
              style={{ border: 'none', outline: 'none', fontSize: 13, color: 'var(--text-primary)', background: 'transparent', cursor: 'pointer' }}
            >
              {ACTION_FILTERS.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {(roleFilter || actionFilter) && (
            <button
              onClick={() => { setRoleFilter(''); setActionFilter(''); setPage(1) }}
              style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
            >Clear filters</button>
          )}
        </div>

        {/* Table */}
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th>User</th>
                <th>IP Address</th>
                <th>Timestamp</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</td></tr>
              ) : fetchError ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--danger)' }}>{fetchError}</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No audit events found</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 500 }}>
                        <span style={{ color: log.status === 'failed' ? 'var(--danger)' : 'var(--primary-navy)' }}>
                          {ACTION_ICONS[log.action] ?? <Settings size={13} />}
                        </span>
                        {formatAction(log.action)}
                      </div>
                    </td>
                    <td style={{ fontSize: 13 }}>{log.user_email || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{log.ip_address || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                    <td>
                      <span className={`badge ${log.status === 'failed' ? 'badge-danger' : 'badge-success'}`}>
                        {log.status === 'failed'
                          ? <><XCircle size={11} /> Failed</>
                          : <><CheckCircle size={11} /> Success</>}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{total} event{total !== 1 ? 's' : ''} total</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <span style={{ padding: '6px 12px', fontSize: 13, color: 'var(--text-muted)' }}>{page} / {totalPages}</span>
              <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @media (max-width: 768px) {
          .asc-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .asc-stats-grid { gap: 10px !important; }
          .asc-stats-grid .stat-card { padding: 14px 12px !important; }
          .asc-stats-grid .icon { width: 38px !important; height: 38px !important; }
          .asc-stats-grid .value { font-size: 20px !important; }
        }
      `}</style>
    </div>
  )
}
